import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Table, type TableColumn } from '../../../../components/table/table';
import { Text } from '../../../../ui-library/typography/typography';
import { sortableTime, type UnifiedDocument } from './agent-document.helpers';
import {
  type DocumentActionHandlers,
  DocumentActions,
  DocumentSourceBadge,
  isFloraPreviewable,
} from './document-actions';

interface DocumentTableProps {
  documents: UnifiedDocument[];
  handlers: DocumentActionHandlers;
  /** Primary row click (Google → preview, Flora → download). */
  onPrimary: (doc: UnifiedDocument) => void;
}

/**
 * List-view of documents built on the shared sortable {@link Table}. Columns:
 * Name, Type, Source, Modified, Size, and per-row actions. Defaults to
 * most-recently-modified first; rows are clickable for the primary action.
 *
 * @param {DocumentTableProps} props - The documents, action handlers, and primary click.
 * @returns {JSX.Element} The document table.
 */
export function DocumentTable({ documents, handlers, onPrimary }: DocumentTableProps) {
  const columns: TableColumn<UnifiedDocument>[] = [
    {
      key: 'name',
      label: 'Name',
      sortValue: (row) => row.name.toLowerCase(),
      render: (_value, row) => (
        <NameCell>
          <IconBox style={{ background: `${row.typeColor}14` }}>
            <Icon
              icon={row.typeIcon}
              width={18}
              height={18}
              color={row.source === 'google' ? row.typeColor : undefined}
            />
          </IconBox>
          <NameText size="small" weight="medium" color="var(--ink-dark)" title={row.name}>
            {row.name}
          </NameText>
          {/* Matches the grid's version chip (see `document-card.tsx`): only a
              Flora-generated Word/Excel doc has browsable revisions, and only
              when there is more than one (revision > 1) — a first-and-only
              version has nothing to browse. Uploaded files are excluded too. */}
          {isFloraPreviewable(row) && row.revision != null && row.revision > 1 && <RevTag>v{row.revision}</RevTag>}
        </NameCell>
      ),
    },
    {
      key: 'typeLabel',
      label: 'Type',
      render: (_value, row) => (
        <TypeCell>
          <ColorDot style={{ background: row.typeColor }} />
          <Text size="small" color="var(--ink-dark)">
            {row.typeLabel}
          </Text>
        </TypeCell>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      render: (_value, row) => <DocumentSourceBadge doc={row} />,
    },
    {
      key: 'modifiedTime',
      label: 'Modified',
      sortValue: (row) => sortableTime(row.modifiedTime),
      render: (_value, row) => (
        <Text size="small" color="var(--ink-lighter)">
          {row.modifiedLabel}
        </Text>
      ),
    },
    {
      key: 'sizeLabel',
      label: 'Size',
      sortValue: (row) => row.flora?.file_size ?? -1,
      render: (_value, row) => (
        <Text size="small" color="var(--ink-lighter)">
          {row.sizeLabel}
        </Text>
      ),
    },
    {
      key: 'id',
      label: '',
      sortable: false,
      width: '96px',
      render: (_value, row) => (
        // Stop propagation so action clicks don't also fire the row's primary click.
        <ActionsCell onClick={(event) => event.stopPropagation()}>
          <DocumentActions doc={row} handlers={handlers} />
        </ActionsCell>
      ),
    },
  ];

  return (
    <Table<UnifiedDocument>
      data={documents}
      columns={columns}
      getRowId={(row) => row.id}
      onRowClick={onPrimary}
      defaultSortedBy="modifiedTime"
      defaultSortedByDirection="desc"
      pageSize={Math.max(documents.length, 1)}
      totalItems={documents.length}
      stickyHeader
    />
  );
}

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconBox = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NameText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RevTag = styled.span`
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 5px;
  background: var(--secondary-lightest);
  color: var(--secondary-darkest);
  font-size: 10px;
  font-weight: 700;
`;

const TypeCell = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

const ColorDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
`;
